import * as d3 from 'd3'
import { useMemo } from 'react'

import {
  type ChartControlsState,
  ChartTooltip,
  useChartBase,
} from '@/components/ui/chart'
import type { MerchantStat } from '@/modules/projects/stores/types'
import {
  cn,
  numberToCompactCurrency,
  numberToCurrency,
  semanticColors,
} from '@/utils'

const MARGIN = { top: 16, right: 24, bottom: 44, left: 64 }
const POINT_RADIUS = 5

interface FrequencyAmountScatterProps {
  merchants: MerchantStat[]
  onMerchantClick?: (merchantGroup: string) => void
  onChartReady?: (params: ChartControlsState) => void
}

export function FrequencyAmountScatter({
  merchants,
  onMerchantClick,
  onChartReady,
}: FrequencyAmountScatterProps) {
  const {
    containerRef,
    dimensions,
    isFullscreen,
    toggleFullscreen,
    hoveredData,
    setHoveredData,
  } = useChartBase(onChartReady)

  const handleMerchantClick = async (merchantGroup: string) => {
    if (isFullscreen) await toggleFullscreen()
    onMerchantClick?.(merchantGroup)
  }

  const points = useMemo(
    () =>
      merchants.filter(
        (merchant) => merchant.count > 0 && Math.abs(merchant.amount) > 0,
      ),
    [merchants],
  )

  const { xScale, yScale } = useMemo(() => {
    if (!dimensions.width || !dimensions.height || points.length === 0)
      return {}
    const maxCount = d3.max(points, (merchant) => merchant.count) ?? 1
    const amounts = points.map((merchant) => Math.abs(merchant.amount))
    const minAmount = d3.min(amounts) ?? 1
    const maxAmount = d3.max(amounts) ?? 1

    const x = d3
      .scaleLinear()
      .domain([0, maxCount])
      .nice()
      .range([MARGIN.left, dimensions.width - MARGIN.right])

    const y = d3
      .scaleLog()
      .domain([Math.max(minAmount, 1), Math.max(maxAmount, 10)])
      .nice()
      .range([dimensions.height - MARGIN.bottom, MARGIN.top])

    return { xScale: x, yScale: y }
  }, [dimensions.width, dimensions.height, points])

  if (points.length === 0) {
    return (
      <div className="flex h-40 items-center justify-center text-muted-foreground text-sm">
        No merchant data.
      </div>
    )
  }

  const xTicks = xScale?.ticks(6) ?? []
  // Log-scale ticks — only decades ($1, $10, $100, ...). d3's .ticks() returns
  // every minor tick per decade, which overlap on short y-axes.
  const yTicks = yScale
    ? d3
        .range(
          Math.floor(Math.log10(yScale.domain()[0])),
          Math.ceil(Math.log10(yScale.domain()[1])) + 1,
        )
        .map((exponent) => 10 ** exponent)
    : []
  const hovered = hoveredData as MerchantStat | null

  return (
    <div
      ref={containerRef}
      className={cn(
        'group relative h-full w-full',
        isFullscreen && 'bg-white p-6',
      )}
    >
      {dimensions.width > 0 && dimensions.height > 0 && xScale && yScale && (
        <svg
          role="img"
          aria-label="Merchant transaction frequency vs amount"
          width={dimensions.width}
          height={dimensions.height}
          className="overflow-visible"
        >
          <g>
            {yTicks.map((tick) => {
              const tickY = yScale(tick)
              return (
                <g key={`y-${tick}`}>
                  <line
                    x1={MARGIN.left}
                    x2={dimensions.width - MARGIN.right}
                    y1={tickY}
                    y2={tickY}
                    stroke="currentColor"
                    strokeOpacity={0.08}
                  />
                  <text
                    x={MARGIN.left - 8}
                    y={tickY}
                    textAnchor="end"
                    dominantBaseline="middle"
                    className="fill-muted-foreground text-[10px]"
                  >
                    {numberToCompactCurrency(tick)}
                  </text>
                </g>
              )
            })}
          </g>

          <g>
            {xTicks.map((tick) => {
              const tickX = xScale(tick)
              return (
                <text
                  key={`x-${tick}`}
                  x={tickX}
                  y={dimensions.height - MARGIN.bottom + 16}
                  textAnchor="middle"
                  className="fill-muted-foreground text-[10px]"
                >
                  {tick}
                </text>
              )
            })}
            <text
              x={(dimensions.width + MARGIN.left - MARGIN.right) / 2}
              y={dimensions.height - 6}
              textAnchor="middle"
              className="fill-muted-foreground text-[10px]"
            >
              Transaction count
            </text>
          </g>

          <g>
            <text
              transform={`translate(12, ${(dimensions.height + MARGIN.top - MARGIN.bottom) / 2}) rotate(-90)`}
              textAnchor="middle"
              className="fill-muted-foreground text-[10px]"
            >
              Amount (log)
            </text>
          </g>

          <g>
            {points.map((merchant) => {
              const cx = xScale(merchant.count)
              const cy = yScale(Math.max(Math.abs(merchant.amount), 1))
              const color =
                merchant.amount >= 0
                  ? semanticColors.inflow
                  : semanticColors.outflow
              const isHovered =
                hovered?.merchantGroup === merchant.merchantGroup
              return (
                <circle
                  key={merchant.merchantGroup}
                  cx={cx}
                  cy={cy}
                  r={isHovered ? POINT_RADIUS + 2 : POINT_RADIUS}
                  fill={color}
                  fillOpacity={0.6}
                  stroke={color}
                  strokeWidth={isHovered ? 2 : 1}
                  className="cursor-pointer transition-all"
                  onPointerEnter={() => setHoveredData(merchant)}
                  onPointerLeave={() => setHoveredData(null)}
                  onClick={() => handleMerchantClick(merchant.merchantGroup)}
                />
              )
            })}
          </g>
        </svg>
      )}

      <ChartTooltip
        active={!!hovered}
        position={
          hovered && xScale && yScale
            ? {
                x: xScale(hovered.count),
                y: yScale(Math.max(Math.abs(hovered.amount), 1)),
              }
            : undefined
        }
      >
        {hovered && (
          <>
            <div className="font-semibold capitalize">
              {hovered.merchantGroup}
            </div>
            <div className="text-muted-foreground opacity-80">
              {numberToCurrency(hovered.amount)} · {hovered.count} txns
            </div>
          </>
        )}
      </ChartTooltip>
    </div>
  )
}
